// @ts-strict-ignore
import { useSelector } from 'react-redux';
import type { ReactNode } from 'react';
import { Fragment, useMemo } from 'react';
import {
  selectActiveNodeEditorTab,
  selectDiffedActions,
  selectFocusedActionForDiff,
  selectInitialWorkflowData,
  selectPublicVersion,
  selectWorkflowData,
} from 'flow/store/redux/selectors';
import { useInitialWorkflowStore } from './useInitialWorkflowStore';
import {
  StyledDiffViewContainer,
  StyledDiffViewPane,
  StyledBadgeContainer,
  StyledDiffViewGrid,
  StyledDiffViewCardSlot,
} from './styles';
import { Badge } from '@usealloy/component-library';
import { StyledCodeStyleText } from 'flow/editor/styles';
import type { Pane } from './DiffContext';
import DiffContextProvider, { panes } from './DiffContext';
import { Operation } from 'flow/service/changelog/types';
import type { Action, SchemaInstance } from 'flow/store/engine/types';
import { DiffCard } from './DiffCard';
import { ActionType } from 'flow/store/engine/constants';

type DiffViewSplitProps = {
  children: ReactNode;
  action?: Action;
  showBadges?: boolean;
};

export { getDiffStatusById } from './DiffContext';

/**
 * Renders `children` twice, side by side: "Existing" against a store/form scoped to the
 * initial snapshot, and "Modified" against the ambient store/form. Each pane is wrapped in
 * its own DiffContextProvider so children can diff against each other.
 */
export function DiffViewSplit({ children, action, showBadges = true }: DiffViewSplitProps) {
  const initialWorkflowData = useSelector(selectInitialWorkflowData);
  const currentWorkflowData = useSelector(selectWorkflowData);
  const activeNodeEditorTab = useSelector(selectActiveNodeEditorTab);
  const selectedFocusedAction = useSelector(selectFocusedActionForDiff);
  const diffedActions = useSelector(selectDiffedActions);
  const initialStore = useInitialWorkflowStore();
  const workflowVersion = useSelector(selectPublicVersion);


  const focusedPublicId = action?.publicId ?? selectedFocusedAction?.publicId;
  const initialFocusedAction = initialWorkflowData?.actions?.[focusedPublicId];
  const currentFocusedAction = action ?? currentWorkflowData?.actions?.[focusedPublicId];

  // Field-level highlighting only makes sense for a MODIFIED action
  const focusedActionDiffStatus = diffedActions[focusedPublicId];
  const highlightFields = focusedActionDiffStatus === Operation.Modify;

  function renderCorrectBadge(pane: Pane, actionDiffStatus: Operation) {
    if (pane === panes.left) {
      if (actionDiffStatus === Operation.Modify) return <Badge status="neutral">Existing</Badge>;
      if (actionDiffStatus === Operation.Remove) return <Badge status="danger">Removed</Badge>;
      if (actionDiffStatus === Operation.Add) {
        return (
          <StyledCodeStyleText>This node was added in v{workflowVersion + 1}.</StyledCodeStyleText>
        );
      }
      return '';
    }

    if (actionDiffStatus === Operation.Modify) return <Badge status="attention">Modified</Badge>;
    if (actionDiffStatus === Operation.Add) return <Badge status="success">Added</Badge>;
    if (actionDiffStatus === Operation.Remove) {
      return (
        <StyledCodeStyleText>This node was removed in v{workflowVersion + 1}.</StyledCodeStyleText>
      );
    }
    return '';
  }

  return (
    <StyledDiffViewContainer>
      <StyledDiffViewPane>
        <DiffContextProvider
          highlightFields={highlightFields}
          pane={panes.left}
        >
          {showBadges && (
          <StyledBadgeContainer>
            {renderCorrectBadge(panes.left, focusedActionDiffStatus)}
          </StyledBadgeContainer>
          )}
          <DiffCard
            action={initialFocusedAction}
            pane={panes.left}
            store={initialStore}
            formTab={activeNodeEditorTab}
            child={children}
          />
        </DiffContextProvider>
      </StyledDiffViewPane>
      <StyledDiffViewPane>
        <DiffContextProvider
          highlightFields={highlightFields}
          pane={panes.right}
        >
          {showBadges && (
            <StyledBadgeContainer>
              {renderCorrectBadge(panes.right, focusedActionDiffStatus)}
            </StyledBadgeContainer>
          )}
          <DiffCard
            action={currentFocusedAction}
            pane={panes.right}
            formTab={activeNodeEditorTab}
            child={children}
          />
        </DiffContextProvider>
      </StyledDiffViewPane>
    </StyledDiffViewContainer>
  );
}

type DiffRow = {
  id: string;
  current?: Action;
  initial?: Action;
};

type DiffViewActionsSplitProps = {
  children: (action: Action) => ReactNode;
  showBadges?: boolean;
};

function getOutcomeActionIds(workflowData: Partial<SchemaInstance>) {
  return Object.values(workflowData?.actions ?? {})
    .filter(action => action.type === ActionType.OUTCOME)
    .map(action => action.publicId);
}

/**
 * Diffs every outcome action side by side: one badge row per pane, then one aligned row
 * per outcome. Outcomes present on only one side (added or removed) render an empty slot
 * on the other, so paired rows stay level with each other.
 */
export function DiffViewOutcomeActionsSplit({
  children,
  showBadges = true,
}: DiffViewActionsSplitProps) {
  const initialWorkflowData = useSelector(selectInitialWorkflowData);
  const currentWorkflowData = useSelector(selectWorkflowData);
  const initialStore = useInitialWorkflowStore();

  const rows = useMemo<DiffRow[]>(() => {
    const allActionIds = new Set<string>([
      ...getOutcomeActionIds(initialWorkflowData),
      ...getOutcomeActionIds(currentWorkflowData),
    ]);

    return [...allActionIds]
      .map(id => ({
        id,
        initial: initialWorkflowData?.actions?.[id],
        current: currentWorkflowData?.actions?.[id],
      }));
  }, [initialWorkflowData, currentWorkflowData]);

  return (
    <StyledDiffViewGrid>
      {showBadges && (
        <>
          <StyledDiffViewCardSlot pane={panes.left}>
            <StyledBadgeContainer>
              <Badge status="neutral">Existing</Badge>
            </StyledBadgeContainer>
          </StyledDiffViewCardSlot>
          <StyledDiffViewCardSlot pane={panes.right}>
            <StyledBadgeContainer>
              <Badge status="attention">Modified</Badge>
            </StyledBadgeContainer>
          </StyledDiffViewCardSlot>
        </>
      )}
      {rows.map(row => (
        <Fragment key={row.id}>
          <DiffContextProvider pane={panes.left}>
            <DiffCard
              action={row.initial}
              child={row.initial ? children(row.initial) : null}
              pane={panes.left}
              store={initialStore}
            />
          </DiffContextProvider>
          <DiffContextProvider pane={panes.right}>
            <DiffCard
              action={row.current}
              child={row.current ? children(row.current) : null}
              pane={panes.right}
            />
          </DiffContextProvider>
        </Fragment>
      ))}
    </StyledDiffViewGrid>
  );
}
